import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaAction } from 'src/app/model/mtma-action';
import { CustomValidators } from 'src/app/validators/custom.validators';
import { MtmaTag } from 'src/app/model/mtma-tag';
import * as moment from 'moment';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { selectMtmaTagModify, selectMtmaActions, selectMtmaRulesAll } from 'src/app/store/device/device.selector';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { TranslateService } from '@ngx-translate/core';
import { MtmaRule } from 'src/app/model/mtma-rules-access';

@Component({
	selector: 'app-mtma-manage-tag-dialog',
	templateUrl: './mtma-manage-tag-dialog.component.html',
	providers: [{
		provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
	}]
})

export class MtmaTagDialogComponent implements OnInit, OnDestroy {
	tagForm: FormGroup;

	mtmaTagModify$: Observable<boolean | undefined>;
	mtmaTagModifySub: Subscription | undefined;

	tag: MtmaTag;

	adding = true;

	mtmaActions: MtmaAction[] = [];
	mtmaActions$: Observable<MtmaAction[] | undefined>;
	mtmaActionsSub: Subscription | undefined;

	mtmaRules: MtmaRule[];
	mtmaRules$: Observable<MtmaRule[] | undefined>;
	mtmaRulesSub: Subscription | undefined;

	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService,
	) {
		if (config.data !== undefined && this.config.data.tag !== undefined) {
			this.tag = this.config.data.tag;
			this.adding = false;
		} else {
			this.tag = new MtmaTag();
		}
		this.initSelector();
	}

	ngOnInit(): void {

		this.store.dispatch(DeviceActions.getMtmaRulesAll());

		this.subscribeAll();
		this.buildForm();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	initSelector() {
		this.mtmaTagModify$ = this.store.select(selectMtmaTagModify);
		this.mtmaActions$ = this.store.select(selectMtmaActions);
		this.mtmaRules$ = this.store.select(selectMtmaRulesAll);
	}

	subscribeAll() {
		this.mtmaTagModifySub = this.mtmaTagModify$
			.pipe(filter(mtmaTagModify => !!mtmaTagModify))
			.subscribe(() => {
				this.resetClose();
			}
			);
		this.mtmaActionsSub = this.mtmaActions$
			.subscribe(mtmaActions => {
				this.mtmaActions = mtmaActions.filter(a => a.Tags);
			}
			);
		this.mtmaRulesSub = this.mtmaRules$
			.subscribe(listRules => {
				if (listRules === undefined) {
					this.mtmaRules = [];
				} else {
					this.mtmaRules = Object.assign([], listRules);
				}
				const rule = new MtmaRule();
				rule.Name = this.translate.instant('NO_GROUP');
				rule.Id = null;
				this.mtmaRules.unshift(rule);
			}
			);
	}

	ngOnDestroy(): void {
		this.mtmaTagModifySub?.unsubscribe();
		this.mtmaActionsSub?.unsubscribe();
		this.mtmaRulesSub?.unsubscribe();
	}

	buildForm() {
		this.tagForm = new FormGroup({
			'Code': new FormControl(this.tag.Code, [Validators.required, Validators.minLength(4),
			Validators.maxLength(12), Validators.pattern('[0-9a-fA-F]+')]),
			'Description': new FormControl(this.tag.Description, [Validators.minLength(1), Validators.maxLength(48)]),
			'Action1': new FormControl(this.tag.Actions[0], Validators.required),
			'Action2': new FormControl(this.tag.Actions[1], Validators.required),
			'Rule': new FormControl(this.tag.RuleId),
			'StartDate': new FormControl(this.tag.DtFrom === undefined ? null : moment(this.tag.DtFrom).toDate()),
			'StartTime': new FormControl(this.getTime(this.tag.DtFrom)),
			'EndDate': new FormControl(this.tag.DtTo === undefined ? null : moment(this.tag.DtTo).toDate()),
			'EndTime': new FormControl(this.getTime(this.tag.DtTo)),
		});
		this.tagForm.addValidators(CustomValidators.checkMatchingMtmaActionContact('Action1', 'Action2'));
		this.tagForm.addValidators(CustomValidators.checkMatchingTemporaryRule('Rule', 'StartTime', 'EndTime', 'StartDate', 'EndDate'));
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetTagForm());
		this.closeDialog();
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.tagForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.tagForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save(): void {
		let startTime = ''
		if (typeof this.f.StartTime.value === 'string') {
			startTime = this.f.StartTime.value;
		} else {
			startTime = moment(this.f.StartTime.value).format('HH:mm');
		}
		let endTime = ''
		if (typeof this.f.EndTime.value === 'string') {
			endTime = this.f.EndTime.value;
		} else {
			endTime = moment(this.f.EndTime.value).format('HH:mm');
		}
		this.tag = {
			...this.tag,
			Description: this.f.Description.value,
			Code: this.f.Code.value,
			Actions: [this.f.Action1.value, this.f.Action2.value],
			RuleId: this.f.Rule.value === undefined || this.f.Rule.value === null ? 0 : this.f.Rule.value
		};
		if (this.tag.RuleId !== null && this.tag.RuleId !== undefined &&
			this.tagForm.value.StartDate != null && this.tagForm.value.StartTime != null &&
			this.tagForm.value.EndDate != null && this.tagForm.value.EndTime != null) {
			//DATE VAL
			this.tag.DtFrom = moment(this.tagForm.value.StartDate).format('yyyy-MM-DDT') + startTime;
			this.tag.DtTo = moment(this.tagForm.value.EndDate).format('yyyy-MM-DDT') + endTime;
		} else {
			this.tag.DtFrom = undefined;
			this.tag.DtTo = undefined;
		}
		if (this.tag.ObjId) {
			this.store.dispatch(DeviceActions.editMtmaTag({ tag: this.tag }));
		} else {
			if (this.tag.RuleId === 0) {
				this.tag.RuleId = null;
			}
			this.store.dispatch(DeviceActions.addMtmaTag({ tag: this.tag }));
		}
	}

	getTime(date: string): string {
		if (date === undefined) {
			return '';
		}
		return moment(date).format('HH:mm');
	}

	get f(): any {
		return this.tagForm.controls;
		// return (this.tagForm.controls !== undefined) ? this.tagForm.controls : this.tagForm;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: MtmaTagDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: MtmaTagDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	dynamicInputTextDesktopTabletWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-30rem');
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

}
